import { useContext } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Card, F0Icon } from '@factorialco/f0-react'
import { Check, Rocket } from '@factorialco/f0-react/icons/app'
import { EnvironmentContext } from 'config/environment'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  CanSeeModulesMarketplaceDocument,
  CanSelfUpsellDocument,
  GetFeatureAvailabilitiesDocument,
} from 'generated/resources/graphql'

import { useModuleAvailabilities } from 'modules/core/hooks/useModuleAvailabilities'
import type { ModuleAvailabilityType } from 'modules/core/hooks/useModuleAvailability'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { PricingPlanItemProps } from 'modules/marketplace_modules/components/Revamp/types'
import { isModuleRevampEnabled } from 'modules/marketplace_modules/components/utils/isModuleRevampEnabled'
import {
  allProjectFeatures,
  isProjectBusiness,
} from 'modules/marketplace_modules/components/utils/isProjectBusiness'
import { BUSINESS_VARIANT, ENTERPRISE_VARIANT } from 'modules/upsell/lib/selfUpsell/plans/utils'

export const Item = ({
  plan,
  onViewAllFeatures,
  onGetItNow,
  onRequestUpsell,
  moduleSlug,
  chargebeeModuleName,
  isAuthorized,
}: PricingPlanItemProps) => {
  const perksArray = plan.perks ? plan.perks.split('\n').filter(Boolean) : []
  const { data: selfUpsellPolicy } = useQuery(CanSelfUpsellDocument)
  const { data: moduleAuthData } = useQuery(CanSeeModulesMarketplaceDocument)
  const { moduleAvailabilities } = useModuleAvailabilities()
  const { ignoreUpsell } = useContext(EnvironmentContext)

  const isProjectsModule = chargebeeModuleName === AvailableModules.projects

  const isRevampEnabledModule = isModuleRevampEnabled(chargebeeModuleName)

  const { data: featureData } = useQuery(GetFeatureAvailabilitiesDocument, {
    variables: {
      modules: [],
      ids: allProjectFeatures,
    },
    fetchPolicy: 'cache-first',
    skip: !isRevampEnabledModule,
  })

  const featureAvailabilities = featureData?.companies.featureAvailabilities ?? []
  const isProjectBusinessResult = isProjectsModule
    ? isProjectBusiness(featureAvailabilities)
    : false

  const canSeeProjectsUpsell = isProjectBusinessResult

  const canSelfUpsell = selfUpsellPolicy?.billing._policies.subscriptionUpgradeSubscription.can
  const canUpsell = moduleAuthData?.modules._policies.moduleManageModule.can
  const isAlreadyInstalled =
    moduleAvailabilities.find((module: ModuleAvailabilityType) => module.id === moduleSlug)
      ?.available && !canSeeProjectsUpsell

  const getPrimaryAction = () => {
    if (!isAuthorized) return undefined

    const showSelfUpsellButton = canSelfUpsell || ignoreUpsell

    if (showSelfUpsellButton) {
      return {
        label: i18n.t('marketplace.revamp.get_it_now'),
        onClick: onGetItNow || (() => {}),
      }
    }
    if (canUpsell) {
      return {
        label: i18n.t('marketplace.revamp.request_upsell'),
        onClick: onRequestUpsell || (() => {}),
      }
    }

    return undefined
  }

  return (
    <F0Card
      avatar={{
        type: 'emoji',
        emoji: plan.icon || '❓',
      }}
      title={
        plan.name === BUSINESS_VARIANT
          ? i18n.t('marketplace.revamp.business')
          : plan.name === ENTERPRISE_VARIANT
          ? i18n.t('marketplace.revamp.enterprise')
          : plan.name || ''
      }
      description={canSelfUpsell && !plan.hasDiscount ? plan.price || '' : ''}
      primaryAction={isAlreadyInstalled ? undefined : getPrimaryAction()}
      secondaryActions={[
        {
          label: i18n.t('marketplace.revamp.view_all_features'),
          onClick: onViewAllFeatures || (() => {}),
        },
      ]}
    >
      <Box gap="s8" paddingBottom="s8">
        {canSelfUpsell && plan.hasDiscount && plan.originalPrice && (
          <Box gap="s2">
            <Box flexDirection="row" gap="s2">
              <Text size="200" color="grey700" stroked>
                {plan.originalPrice}
              </Text>
              <Text size="200" color="grey700">
                {plan.price}
              </Text>
            </Box>

            <Box flexDirection="row" gap="s2" paddingBottom="s8">
              {plan.seatOriginalPrice && (
                <Text size="200" color="grey700" stroked>
                  {plan.seatOriginalPrice}
                </Text>
              )}
              {plan.seatDescription && (
                <Text size="200" color="grey700">
                  {plan.seatDescription}
                </Text>
              )}
            </Box>
          </Box>
        )}
        <Box flexDirection="row" gap="s8" alignItems="flexStart" paddingY="s8">
          <Box flexDirection="row" gap="s8" alignItems="flexStart">
            <F0Icon icon={Rocket} size="md" paddingTop="s2" color="default" />
          </Box>
          <Text size="200" color="black" ellipsis lineClamp={1}>
            {plan.name === BUSINESS_VARIANT
              ? i18n.t('marketplace.revamp.key_features')
              : plan.name === ENTERPRISE_VARIANT
              ? i18n.t('marketplace.revamp.all_features')
              : plan.title}
          </Text>
        </Box>
        <Box gap="s16">
          {perksArray.map((perk, index) => (
            <Box key={index} flexDirection="row" gap="s8" alignItems="flexStart">
              {perk.trim() ? (
                <>
                  <F0Icon icon={Check} size="md" color="default" />
                  <Text size="200" color="black" ellipsis lineClamp={1}>
                    {perk}
                  </Text>
                </>
              ) : (
                <Box style={{ height: '20px' }} />
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </F0Card>
  )
}
